import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { getUsers, createUser, updateUser, deleteUser } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, Edit, Trash2, Loader2, Users, Search, X, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingPercent, setLoadingPercent] = useState(0);
  const percentTimerRef = useRef(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', whatsapp: '+55', password: '', role: 'USER_READ' });
  const [submitting, setSubmitting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  // Estado de validação do telefone: null = ok/sem erro, 'format_error' = formato inválido.
  const [phoneStatus, setPhoneStatus] = useState(null);

  // Filtros
  const [nameFilter, setNameFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Efeito para animação de loading
  useEffect(() => {
    if (loading) {
      setLoadingPercent(0);
      percentTimerRef.current = setInterval(() => {
        setLoadingPercent(prev => {
          if (prev >= 95) return 95;
          const increment = Math.max(0.5, (95 - prev) / 20);
          return Math.min(95, prev + increment);
        });
      }, 100);
    } else {
      setLoadingPercent(100);
      if (percentTimerRef.current) {
        clearInterval(percentTimerRef.current);
        percentTimerRef.current = null;
      }
    }
    return () => {
      if (percentTimerRef.current) clearInterval(percentTimerRef.current);
    };
  }, [loading]);

  // Verificar se pode deletar um usuário
  const canDeleteUser = (targetUser) => {
    // Não pode deletar a si mesmo
    if (currentUser?._id === targetUser._id) return false;
    // Não pode deletar o tenant owner
    if (targetUser.is_tenant_owner) return false;
    // Se é o owner, pode deletar qualquer um (exceto a si mesmo)
    if (currentUser?.is_tenant_owner) return true;
    // Se não é owner, não pode deletar outros ADMINs
    if (targetUser.role === 'ADMIN') return false;
    return true;
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await getUsers();
      // Ordenar por nome alfabeticamente
      const sortedUsers = response.data.sort((a, b) => 
        (a.name || '').localeCompare(b.name || '', 'pt-BR', { sensitivity: 'base' })
      );
      setUsers(sortedUsers);
    } catch (error) {
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        whatsapp: user.whatsapp || '+55',
        password: '',
        role: user.role,
      });
    } else {
      setEditingUser(null);
      setFormData({ name: '', email: '', whatsapp: '+55', password: '', role: 'USER_READ' });
    }
    setPhoneStatus(null);
    setDialogOpen(true);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhoneFormat = (phone) => {
    // International format: + followed by 10-15 digits
    const phoneRegex = /^\+\d{10,15}$/;
    return phoneRegex.test(phone.replace(/[\s\-()]/g, ''));
  };

  const handlePhoneChange = (value) => {
    setFormData({ ...formData, whatsapp: value });
    setPhoneStatus(null);
  };

  const handleToggleActive = async (user) => {
    if (user._id === currentUser?._id) {
      toast.error('Você não pode desabilitar seu próprio usuário');
      return;
    }
    try {
      await updateUser(user._id, { active: !user.active });
      toast.success(user.active ? 'Usuário desabilitado' : 'Usuário habilitado');
      fetchUsers();
    } catch (error) {
      toast.error('Erro ao alterar status do usuário');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateEmail(formData.email)) {
      toast.error('E-mail inválido. Use o formato: nome@dominio.com');
      return;
    }

    // Valida só o formato do número (não verificamos mais existência no WhatsApp).
    const cleaned = formData.whatsapp?.replace(/[\s\-()]/g, '') || '';
    if (cleaned && cleaned !== '+55') {
      if (!validatePhoneFormat(cleaned)) {
        setPhoneStatus('format_error');
        toast.error('Número inválido. Use o formato internacional: +5511999999999');
        return;
      }
      setPhoneStatus(null);
    }
    
    setSubmitting(true);

    try {
      if (editingUser) {
        await updateUser(editingUser._id, {
          name: formData.name,
          email: formData.email,
          whatsapp: formData.whatsapp,
          password: formData.password || undefined,
          role: formData.role,
        });
        toast.success('Usuário atualizado');
      } else {
        await createUser(formData);
        toast.success('Usuário criado');
      }

      setDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar usuário');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;

    try {
      await deleteUser(userToDelete._id);
      toast.success('Usuário excluído');
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      toast.error('Erro ao excluir usuário');
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      ADMIN: 'bg-purple-100 text-purple-800',
      USER_EDITOR: 'bg-blue-100 text-blue-800',
      USER_SEND: 'bg-teal-100 text-teal-800',
      USER_READ: 'bg-slate-200 text-slate-800',
    };
    const labels = {
      ADMIN: 'Administrador',
      USER_EDITOR: 'Editor',
      USER_SEND: 'Envio',
      USER_READ: 'Somente Leitura',
    };
    return (
      <Badge className={styles[role] || styles.USER_READ}>
        {labels[role] || role}
      </Badge>
    );
  };

  // Filtrar usuários
  const filteredUsers = users.filter((user) => {
    // Filtro por nome
    if (nameFilter && !user.name.toLowerCase().includes(nameFilter.toLowerCase())) {
      return false;
    }
    // Filtro por email
    if (emailFilter && !user.email.toLowerCase().includes(emailFilter.toLowerCase())) {
      return false;
    }
    // Filtro por função
    if (roleFilter && user.role !== roleFilter) {
      return false;
    }
    // Filtro por status
    if (statusFilter) {
      const isActive = statusFilter === 'active';
      if (user.active !== isActive) return false;
    }
    return true;
  });

  const handleClearFilters = () => {
    setNameFilter('');
    setEmailFilter('');
    setRoleFilter('');
    setStatusFilter('');
  };

  return (
    <div className="flex flex-col h-full">
      {/* Filters */}
      <div className="sticky top-0 z-20 bg-background pb-4">
        <div className="flex items-center gap-3">
          {/* Name Filter */}
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Nome..."
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              className="pl-9"
              data-testid="users-name-filter"
            />
          </div>

          {/* Email Filter */}
          <div className="flex-1">
            <Input
              placeholder="E-mail..."
              value={emailFilter}
              onChange={(e) => setEmailFilter(e.target.value)}
              data-testid="users-email-filter"
            />
          </div>

          {/* Role Filter */}
          <div className="flex-1">
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger data-testid="users-role-filter">
                <SelectValue placeholder="Função" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ADMIN">Administrador</SelectItem>
                <SelectItem value="USER_EDITOR">Editor</SelectItem>
                <SelectItem value="USER_SEND">Envio</SelectItem>
                <SelectItem value="USER_READ">Somente Leitura</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Status Filter */}
          <div className="flex-1">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="users-status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Ativo</SelectItem>
                <SelectItem value="inactive">Inativo</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Clear Button */}
          <Button 
            variant="secondary" 
            onClick={handleClearFilters}
            className="bg-gray-200 dark:bg-gray-700 hover:bg-primary hover:text-primary-foreground"
          >
            <X className="w-4 h-4 mr-1" />
            Limpar
          </Button>

          {/* New User Button */}
          <Button onClick={() => handleOpenDialog()} data-testid="users-add-button">
            <Plus className="w-4 h-4 mr-2" />
            Novo Usuário
          </Button>
        </div>
      </div>

      {/* Loading State - Centralizado */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-6 p-8">
            <div className="relative">
              <Loader2 className="w-16 h-16 animate-spin text-primary" />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-sm font-semibold text-primary">
                  {Math.round(loadingPercent)}%
                </span>
              </div>
            </div>
            <p className="text-muted-foreground text-center">Carregando usuários...</p>
          </div>
        </div>
      ) : (
      <>
      {/* Table */}
      <Card className="flex-1 flex flex-col min-h-0 mb-4">
        <CardContent className="p-0 flex-1 flex flex-col min-h-0">
          <Table data-testid="users-table">
            <TableHeader className="sticky top-0 bg-card z-10">
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>E-mail</TableHead>
                <TableHead>WhatsApp</TableHead>
                <TableHead>Função</TableHead>
                <TableHead>Criado em</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[100px]">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="empty-state">
                      <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">
                        {users.length === 0 ? 'Nenhum usuário criado ainda' : 'Nenhum usuário encontrado com os filtros aplicados'}
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow key={user._id} className="hover:bg-primary/5">
                    <TableCell className="font-medium">{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.whatsapp || '-'}</TableCell>
                    <TableCell>{getRoleBadge(user.role)}</TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString('pt-BR') : '-'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={user.active}
                        onCheckedChange={() => handleToggleActive(user)}
                        disabled={user._id === currentUser?._id}
                        className="data-[state=checked]:bg-teal-500"
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(user)}
                        >
                          <Edit />
                        </Button>
                        {canDeleteUser(user) && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(user)}
                            >
                            <Trash2 />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      </>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingUser ? 'Editar Usuário' : 'Novo Usuário'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">E-mail</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  disabled={editingUser?.is_tenant_owner}
                />
                {editingUser?.is_tenant_owner && (
                  <p className="text-xs text-muted-foreground">
                    A conta de email do administrador não pode ser alterada.
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="whatsapp">Número de WhatsApp <span className="text-xs text-muted-foreground font-normal">(ex: +5521999999999)</span></Label>
                <div className="relative">
                  <Input
                    id="whatsapp"
                    type="tel"
                    value={formData.whatsapp}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                    placeholder="+5511999999999"
                    data-testid="user-whatsapp-input"
                    className={
                      phoneStatus === 'format_error' ? 'border-amber-500 pr-8' : ''
                    }
                  />
                  {phoneStatus === 'format_error' && (
                    <AlertCircle className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-500" />
                  )}
                </div>
                {phoneStatus === 'format_error' && (
                  <p className="text-xs text-amber-600">Formato inválido. Use: +5511999999999</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">
                  {editingUser ? 'Nova Senha (deixe em branco para manter)' : 'Senha'}
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingUser}
                  placeholder={editingUser ? '••••••••' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Função</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                  disabled={editingUser?.is_tenant_owner}
                  data-testid="user-role-select"
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USER_READ">Somente Leitura</SelectItem>
                    <SelectItem value="USER_SEND">Envio</SelectItem>
                    <SelectItem value="USER_EDITOR">Editor</SelectItem>
                    <SelectItem value="ADMIN">Administrador</SelectItem>
                  </SelectContent>
                </Select>
                {editingUser?.is_tenant_owner ? (
                  <p className="text-xs text-amber-600">
                    O perfil do administrador principal não pode ser alterado
                  </p>
                ) : (
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p>👁️ <span className="font-semibold">Somente Leitura</span>: visualiza tudo</p>
                    <p>📤 <span className="font-semibold">Envio</span>: envia mensagens</p>
                    <p>✏️ <span className="font-semibold">Editor</span>: cria e edita contatos e campanhas</p>
                    <p>👑 <span className="font-semibold">Administrador</span>: acesso total</p>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingUser ? 'Salvar' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Tem certeza que deseja excluir o usuário <strong>{userToDelete?.name}</strong>?
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
